<template>
  <Dialog v-model:visible="open" modal header="Create A New Project">
    <!-- At project limit: show upgrade prompt instead of form -->
    <div v-if="isAtProjectLimit" class="space-y-4 pb-4">
      <div class="rounded-md bg-red-400/20 px-5 py-4 ring-1 ring-red-400/50 dark:bg-red-900/30 dark:ring-red-600/50">
        <div class="flex items-start gap-3">
          <ExclamationTriangleIcon class="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
          <div class="space-y-1">
            <div class="font-semibold text-red-800 dark:text-red-200"> Project limit reached </div>
            <div class="text-sm text-red-700 dark:text-red-300">
              You are using {{ planLimits.projects.current }} of {{ planLimits.projects.max }} projects on your
              {{ tierDisplayName(planTier) }} plan.
              <template v-if="nextTier">
                Upgrade to {{ tierDisplayName(nextTier) }} for up to {{ nextTierLimit }} projects.
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-end">
        <router-link :to="{ name: ROUTES.UPGRADE }" @click="open = false">
          <Button label="Upgrade Plan" icon="pi pi-arrow-up-right" />
        </router-link>
      </div>
    </div>

    <!-- Normal form when under limit -->
    <form v-else @submit="createTeam" class="space-y-6 pb-8">
      <FormGroup v-if="organizationOptions.length > 1" label="Organization">
        <Select
          v-model="organizationPid"
          :options="organizationOptions"
          option-label="name"
          option-value="pid"
          aria-label="Organization"
          :disabled="isSubmitting"
          class="w-full cursor-pointer"
          size="large" />
        <template v-if="selectedOrganization?.demo_projects" #description>
          <span class="text-sm text-surface-600 dark:text-surface-300" data-test="demo-projects-hint">
            {{ demoProjectsText(selectedOrganization.demo_projects) }} used. A demo project can't be published until it
            has a plan.
          </span>
        </template>
      </FormGroup>

      <FormGroup label="Project Name">
        <InputText v-model="name" :disabled="isSubmitting" class="w-full" size="large" />
        <template #description>
          <span class="text-sm text-red-800">{{ errors.name }}</span>
        </template>
      </FormGroup>

      <FormGroup label="Website URL">
        <InputText
          v-model="website"
          :disabled="isSubmitting"
          :invalid="Boolean(errors.website)"
          :aria-invalid="Boolean(errors.website)"
          class="w-full"
          size="large" />
        <template #description>
          <span v-if="errors.website" role="alert" class="text-sm text-red-800">{{ errors.website }}</span>
          <span v-else-if="websiteResearchWarning" class="text-sm text-surface-600 dark:text-surface-300">
            {{ websiteResearchWarning }}
          </span>
        </template>
      </FormGroup>

      <p v-if="submitError" class="text-sm text-red-800 dark:text-red-300">
        {{ submitError }}
      </p>

      <Button type="submit" :disabled="isSubmitting" :label="isSubmitting ? 'Creating Project…' : 'Create Project'" />
    </form>
  </Dialog>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate'
import { useField } from 'vee-validate'
import z from 'zod'
import { websiteDomainSchema } from '@dashboard/formValidationschemas'
import { toTypedSchema } from '@vee-validate/zod'
import type { TeamCreateAfterCreate } from '@dashboard/stores/teamStore'
import type { TeamRequestError } from '@dashboard/stores/teamStore'
import { isAxiosError } from 'axios'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import type { PlanTier } from '@dashboard/stores/subscriptionStore'
import { getWebsiteResearchIneligibilityReason } from '@dashboard/services/websiteResearchEligibility'
import { requestUuid } from '@dashboard/services/uuid'
import FormGroup from '@dashboard/components/common/FormGroup.vue'
import { useSubscriptionStore } from '@dashboard/stores/subscriptionStore'
import { useTeamStore } from '@dashboard/stores/teamStore'
import { useAuthStore } from '@dashboard/stores/authStore'
import { demoProjectsText } from '@dashboard/modules/settings/services/organizationLabels'
import { isOrganizationManager } from '@dashboard/types/organization'
import Select from 'primevue/select'
import { ROUTES } from '@dashboard/router/routeNames'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import { computed, ref, watch } from 'vue'

const open = defineModel<boolean>()
const props = withDefaults(defineProps<{ afterCreate?: TeamCreateAfterCreate }>(), {
  afterCreate: 'onboarding'
})
const subscriptionStore = useSubscriptionStore()
const { planLimits, planTier, isLegacy, isTrialing, isFreeAccount, isLegacyBilling } = storeToRefs(subscriptionStore)

const TIER_LIMITS: Record<string, number> = {
  grow: 1,
  scale: 3,
  pro: 10
}

const tierDisplayName = (tier: PlanTier): string => {
  const names: Record<PlanTier, string> = {
    grow: 'Grow',
    scale: 'Scale',
    pro: 'Pro',
    legacy: 'Legacy',
    legacy_cta: 'Legacy CTA'
  }
  return names[tier] ?? tier
}

const nextTier = computed<PlanTier | null>(() => {
  if (planTier.value === 'grow') {
    return 'scale'
  }
  if (planTier.value === 'scale') {
    return 'pro'
  }
  return null
})

const nextTierLimit = computed(() => (nextTier.value ? (TIER_LIMITS[nextTier.value] ?? null) : null))

const isAtProjectLimit = computed(() => {
  // Per-team billing: no project limit (each project gets its own subscription, paywall at publish)
  if (!isLegacyBilling.value) {
    return false
  }
  if (isLegacy.value || isTrialing.value || isFreeAccount.value) {
    return false
  }
  return planLimits.value.projects.current >= planLimits.value.projects.max
})

const newTeamSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, 'Project name is required'),
    website: websiteDomainSchema
  })
)

const { handleSubmit, errors, resetForm, setFieldError } = useForm({
  validationSchema: newTeamSchema,
  initialValues: {
    name: '',
    website: ''
  }
})

const { value: name } = useField<string>('name')
const { value: website } = useField<string>('website')
const WEBSITE_RESEARCH_WARNING =
  'We can still create your project, but we won’t be able to research that domain. We’ll start you with generic playbooks, and you can update the website and retry.'
const websiteResearchWarning = computed(() =>
  getWebsiteResearchIneligibilityReason(website.value || '') ? WEBSITE_RESEARCH_WARNING : null
)

const teamStore = useTeamStore()
const { user } = storeToRefs(useAuthStore())

// Only owners and admins can add projects to an organization; the choice
// appears once there is more than one, starting from the open project's.
const organizationOptions = computed(() =>
  (user.value?.organizations ?? []).filter((organization) => isOrganizationManager(organization.role))
)
const defaultOrganizationPid = (): string | null => {
  const current = teamStore.currentTeam?.organization?.pid
  const options = organizationOptions.value
  return options.find((organization) => organization.pid === current)?.pid ?? options[0]?.pid ?? null
}
const organizationPid = ref<string | null>(defaultOrganizationPid())
const selectedOrganization = computed(
  () => organizationOptions.value.find((organization) => organization.pid === organizationPid.value) ?? null
)
watch(organizationOptions, () => {
  if (!selectedOrganization.value) {
    organizationPid.value = defaultOrganizationPid()
  }
})

const submitError = ref<string | null>(null)
const isSubmitting = ref(false)
const createRequestId = ref<string | null>(null)

watch(open, (isOpen) => {
  if (isOpen) {
    return
  }

  submitError.value = null
  isSubmitting.value = false
  createRequestId.value = null
  organizationPid.value = defaultOrganizationPid()
  resetForm()
})

const createTeam = handleSubmit(async (values) => {
  if (isSubmitting.value) {
    return
  }

  isSubmitting.value = true
  submitError.value = null
  createRequestId.value ??= requestUuid()

  try {
    // Always name the organization: without it the server falls back to the open project's, which can differ.
    const organization = selectedOrganization.value
    await teamStore.create(organization ? { ...values, organization_pid: organization.pid } : values, {
      afterCreate: props.afterCreate,
      clientRequestId: createRequestId.value
    })
    open.value = false
    createRequestId.value = null
    resetForm()
  } catch (error) {
    const data = isAxiosError<TeamRequestError>(error) ? error.response?.data : undefined
    // The agency's demo cap is not a billing problem, so it never opens a plan picker.
    if (data?.code === 'demo_project_limit') {
      submitError.value =
        data.message || 'This agency organization has reached its demo project limit. Choose a plan for one first.'
      return
    }
    if (data?.errors?.name?.[0]) {
      setFieldError('name', data.errors.name[0])
    }
    if (data?.errors?.website?.[0]) {
      setFieldError('website', data.errors.website[0])
    }
    submitError.value = data?.message || 'We couldn’t create your project. Please try again.'
  } finally {
    isSubmitting.value = false
  }
})
</script>
